import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { Calendar } from 'lucide-react';
import { SectionCard } from '@/components/common/SectionCard';
import { TextField } from '@/components/forms/TextField';
import { SelectField } from '@/components/forms/SelectField';
import { ComboboxField } from '@/components/forms/ComboboxField';
import { MultiSelectField } from '@/components/forms/MultiSelectField';
import type { CreateStartupFormValues } from '../../../schema';
import {
  FIELD_LABELS,
  FIELD_PLACEHOLDERS,
  FUNDING_STAGE_OPTIONS,
  MARKET_MODEL_OPTIONS,
  MAX_SECTOR_OTHER_LENGTH,
  MAX_SUBSECTORS,
  SECTION_TITLES,
  SECTOR_OTHER_NAME,
  SELECT_PLACEHOLDER,
} from '../../../constants';
import { resolveHqCascade } from '@/lib/utils/hq-location.utils';
import { useSectors } from '../../../hooks/useSectors';
import { useCountries } from '@/hooks/useCountries';
import { useStates } from '@/hooks/useStates';
import { useCitySearch } from '@/hooks/useCitySearch';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { NUMERIC_INPUT_MAX_DIGITS_YEAR } from '@/lib/constants/numeric-input';

interface SectionProps {
  form: UseFormReturn<CreateStartupFormValues>;
}

/**
 * Step 1 "Sector & Stage" card (Figma 287:3098): 3-column dropdown/input grid.
 * Sector/Subsector and HQ Country/State/City are real reference-data
 * dropdowns (MYN-46) - not free text - sourced from `useSectors`/
 * `useCountries`/`useStates`/`useCitySearch` (prefetched on dashboard mount,
 * see `prefetchGeographyAndTaxonomy`). Market Model renders as a single
 * dropdown (doc type "Dropdown") but the form value stays an array for the
 * API contract, so the select maps to [value].
 *
 * HQ Country/State search client-side (`Combobox` fed a locally-filtered
 * slice of the already-fetched list) - both lists are small/bounded enough
 * to hold in memory. Only City goes to the backend (`useCitySearch`), since
 * its table is ~153k rows and can't be prefetched in full; if Country/State
 * ever grow past "known, bounded size", follow that same pattern instead of
 * filtering client-side.
 */
export function SectorStage({ form }: SectionProps) {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const marketModel = watch('marketModels')?.[0] ?? '';
  const sectorId = watch('sectorId');
  const subsectorIds = watch('subsectorIds') ?? [];
  const hqCountryId = watch('hqCountryId');
  const hqStateId = watch('hqStateId');
  const hqCityName = watch('hqCityName');

  const { data: sectors = [] } = useSectors();
  const { data: countries = [] } = useCountries();
  const { states } = useStates(hqCountryId ? Number(hqCountryId) : undefined);

  const sectorOptions = useMemo(
    () => sectors.map((sector) => ({ value: String(sector.id), label: sector.name })),
    [sectors],
  );

  const selectedSector = sectors.find((sector) => String(sector.id) === String(sectorId));
  // "Other" has no subsectors; it asks for the founder's own sector instead (r104).
  const isOtherSector = selectedSector?.name === SECTOR_OTHER_NAME;
  const subsectorOptions = useMemo(() => selectedSector?.subsectors ?? [], [selectedSector]);
  const subsectorMultiSelectOptions = useMemo(
    () =>
      subsectorOptions.map((subsector) => ({ value: String(subsector.id), label: subsector.name })),
    [subsectorOptions],
  );

  const [countryQuery, setCountryQuery] = useState('');
  const selectedCountry = countries.find((country) => String(country.id) === String(hqCountryId));
  // US: Country > State > City. Non-US: Country > City searched country-wide
  // (r105). Changing country already clears any chosen state (effect below),
  // so no stale state is sent; the Backend derives a non-US state from the city.
  const cascade = resolveHqCascade(selectedCountry, hqStateId ? Number(hqStateId) : undefined);
  const countryOptions = useMemo(
    () =>
      countries
        .filter((country) => country.name.toLowerCase().includes(countryQuery.toLowerCase()))
        .map((country) => ({ value: String(country.id), label: country.name })),
    [countries, countryQuery],
  );

  const [stateQuery, setStateQuery] = useState('');
  const selectedState = states.find((state) => String(state.id) === String(hqStateId));
  const stateOptions = useMemo(
    () =>
      states
        .filter((state) => state.name.toLowerCase().includes(stateQuery.toLowerCase()))
        .map((state) => ({ value: String(state.id), label: state.name })),
    [states, stateQuery],
  );

  const [cityQuery, setCityQuery] = useState('');
  // Debounced so a city search fires per typing pause, not per keystroke
  // (the cities table is ~153k rows; this endpoint is the one hit live).
  const debouncedCityQuery = useDebouncedValue(cityQuery);
  const { data: cityResults = [], isFetching: isCitySearching } = useCitySearch(
    cascade.cityScope,
    debouncedCityQuery,
  );
  // Cascading resets: a genuine change to an upstream field invalidates the
  // narrower selections below it. Guard on the PREVIOUS value (not a
  // first-render flag) so the effects fire only on a real user change - never
  // on mount/remount. Step 1 unmounts when the user navigates to a later step
  // (`{step === 0 && ...}` in CreateStartupForm), so a first-render-only guard
  // would still wipe persisted selections every time they clicked Back here.
  const prevSectorId = useRef(sectorId);
  const prevCountryId = useRef(hqCountryId);
  const prevStateId = useRef(hqStateId);

  useEffect(() => {
    if (prevSectorId.current === sectorId) return;
    prevSectorId.current = sectorId;
    setValue('subsectorIds', []);
    // A free-text sector only belongs to "Other"; to-payload relies on this.
    setValue('sectorOther', undefined);
  }, [sectorId, setValue]);

  // hqStateId/hqCityId are Combobox-driven (setValue only, never register()'d
  // - there's no native input for RHF to resync), so resetField can't find a
  // tracked default to fall back to and silently no-ops on them. setValue
  // with an explicit undefined is what actually clears an unregistered field.
  // `optionalWholeNumber` (lib/utils/whole-number-schema.utils.ts) infers as `number | undefined`, so no cast
  // is needed here (pre-2026-08-05 this used `requiredNumber`, a bare
  // `number`, and needed an `as unknown as number` cast to clear it).
  const clearNumberField = useCallback(
    (name: 'hqStateId' | 'hqCityId') => setValue(name, undefined, { shouldDirty: true }),
    [setValue],
  );

  useEffect(() => {
    if (prevCountryId.current === hqCountryId) return;
    prevCountryId.current = hqCountryId;
    clearNumberField('hqStateId');
    clearNumberField('hqCityId');
    setValue('hqCityName', undefined);
    setStateQuery('');
    setCityQuery('');
  }, [hqCountryId, setValue, clearNumberField]);

  useEffect(() => {
    if (prevStateId.current === hqStateId) return;
    prevStateId.current = hqStateId;
    clearNumberField('hqCityId');
    setValue('hqCityName', undefined);
    setCityQuery('');
  }, [hqStateId, setValue, clearNumberField]);

  const toggleSubsector = (value: string) => {
    const id = Number(value);
    const next = subsectorIds.includes(id)
      ? subsectorIds.filter((existing) => existing !== id)
      : [...subsectorIds, id];
    setValue('subsectorIds', next.slice(0, MAX_SUBSECTORS), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <SectionCard title={SECTION_TITLES.SECTOR_STAGE}>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <SelectField
          {...register('sectorId')}
          label={FIELD_LABELS.sector}
          placeholder={SELECT_PLACEHOLDER}
          options={sectorOptions}
          error={errors.sectorId?.message}
        />
        {isOtherSector ? (
          <TextField
            {...register('sectorOther')}
            label={FIELD_LABELS.sectorOther}
            maxLength={MAX_SECTOR_OTHER_LENGTH}
            error={errors.sectorOther?.message}
          />
        ) : (
          <MultiSelectField
            label={FIELD_LABELS.subsectors}
            disabled={subsectorOptions.length === 0}
            options={subsectorMultiSelectOptions}
            selectedValues={subsectorIds.map(String)}
            onToggle={toggleSubsector}
            isOptionDisabled={(value) =>
              !subsectorIds.includes(Number(value)) && subsectorIds.length >= MAX_SUBSECTORS
            }
          />
        )}
        <SelectField
          {...register('fundingStage')}
          label={FIELD_LABELS.fundingStage}
          placeholder={`${FIELD_LABELS.fundingStage} - ${SELECT_PLACEHOLDER}`}
          options={FUNDING_STAGE_OPTIONS}
          error={errors.fundingStage?.message}
        />
        <Controller
          control={control}
          name="yearFounded"
          render={({ field }) => (
            <TextField
              {...field}
              numeric
              groupThousands={false}
              maxDigits={NUMERIC_INPUT_MAX_DIGITS_YEAR}
              label={FIELD_LABELS.yearFounded}
              icon={<Calendar size={16} />}
              placeholder={FIELD_PLACEHOLDERS.yearFounded}
              error={errors.yearFounded?.message}
            />
          )}
        />
        <ComboboxField
          label={FIELD_LABELS.hqCountry}
          inputValue={countryQuery}
          onInputChange={setCountryQuery}
          selectedLabel={selectedCountry?.name}
          options={countryOptions}
          onSelect={(option) => {
            setValue('hqCountryId', Number(option.value), {
              shouldValidate: true,
              shouldDirty: true,
            });
            setCountryQuery('');
          }}
          error={errors.hqCountryId?.message}
        />
        {cascade.showState && (
          <ComboboxField
            label={FIELD_LABELS.hqState}
            disabled={!hqCountryId}
            inputValue={stateQuery}
            onInputChange={setStateQuery}
            selectedLabel={selectedState?.name}
            options={stateOptions}
            onSelect={(option) => {
              setValue('hqStateId', Number(option.value), {
                shouldValidate: true,
                shouldDirty: true,
              });
              setStateQuery('');
            }}
            error={errors.hqStateId?.message}
          />
        )}
        <ComboboxField
          label={FIELD_LABELS.hqCity}
          disabled={cascade.cityScope === undefined}
          inputValue={cityQuery}
          onInputChange={setCityQuery}
          // Stored at selection time (below) - NOT derived from the live
          // `cityResults`, which no longer contains the chosen city once the
          // search query changes, so deriving it there would blank the label.
          selectedLabel={hqCityName}
          loading={isCitySearching}
          options={cityResults.map((city) => ({ value: String(city.id), label: city.name }))}
          onSelect={(option) => {
            setValue('hqCityId', Number(option.value), { shouldValidate: true, shouldDirty: true });
            setValue('hqCityName', option.label);
          }}
          error={errors.hqCityId?.message}
        />
        <SelectField
          label={FIELD_LABELS.marketModels}
          placeholder={`${FIELD_LABELS.marketModels} - ${SELECT_PLACEHOLDER}`}
          options={MARKET_MODEL_OPTIONS}
          value={marketModel}
          onChange={(event) =>
            setValue('marketModels', event.target.value ? [event.target.value] : [], {
              shouldValidate: true,
              shouldDirty: true,
            })
          }
          error={errors.marketModels?.message}
        />
      </div>
    </SectionCard>
  );
}
